import React, { Component } from 'react';
import { Modal } from 'react-bootstrap';
import Axios from 'axios';
import PropTypes from 'prop-types';
import moment from 'moment';
import OkCancelModal from '@/shared/components/OkCancelModal';
import api from '@/shared/api';
import config from '@/config';
import ToastrService from '@/shared/services/ToastrService';
import handleHttpError from '@/shared/services/HandleHttpError';
import { injectIntl } from 'react-intl';
import { CircleCheckIcon, UserAddIcon, UndoIcon, CancelIcon } from '@/shared/components/Icons';
import FormattedListValue, { listItemMsg } from '@/shared/components/FormattedListValue';
import messages from './trainingMessages';
import Checkbox from '@/shared/components/input/Checkbox';
import { AddIcon } from '@/shared/components/Icons';
import TrainingEmployeeModalFilters, {
  defaultTrainingEmployeeModalFilters,
} from './TrainingEmployeeModalFilters';

class TrainingEmployeeModal extends Component {
  constructor(props) {
    super(props);
    const { searchParams = {}, intl } = props;

    const { trainingEmployeeModalFilters: sessionFilters = {}, ...sessionParams } =
      JSON.parse(sessionStorage.getItem('TrainingEmployeeModalParams')) || {};

    this.state = {
      trainingEmployees: [],
      currentSearchParams: undefined,
      allSelected: false,
      initialSearchParams: {
        sortKey: ['firstName', 'lastName'],
        pageSize: -1,
        trainingEmployeeModalFilters: {
          ...defaultTrainingEmployeeModalFilters,
          ...sessionFilters,
          ...searchParams,
        },
        // Override with session storage params
        ...sessionParams,
      },
      saving: false,
    };
  }

  async componentDidMount() {
    if (this.props.show) {
      const trainingId = this.props.training.id;
      this.loadEmployeesWithTraining(
        trainingId,
        this.state.initialSearchParams.trainingEmployeeModalFilters,
      );
    }
  }
  componentDidUpdate(prevProps, prevState) {
    const modalJustOpened = !prevProps.show && this.props.show;
    const filtersChanged =
      this.props.show &&
      prevState.initialSearchParams.trainingEmployeeModalFilters !==
        this.state.initialSearchParams.trainingEmployeeModalFilters;

    if (modalJustOpened || filtersChanged) {
      const trainingId = this.props.training.id;
      this.loadEmployeesWithTraining(
        trainingId,
        this.state.initialSearchParams.trainingEmployeeModalFilters,
      );
    }
  }

  loadEmployeesWithTraining = async (trainingId, filters = {}) => {
    const params = {
      pageNumber: 1,
      pageSize: 1000,
      ...filters,
    };

    this.trainingDataCancelToken = Axios.CancelToken.source();
    try {
      const trainingResponse = await api.base.get(
        `${config.appUrls.api}/api/TrainingEmployee/trainingemployees/${trainingId}`,
        {
          params,
          cancelToken: this.trainingDataCancelToken.token,
        },
      );

      const trainingEmployees = trainingResponse.data.data;

      const trainingEmployeesWithSelected = trainingEmployees.map(emp => ({
        ...emp,
        selected: this.state.allSelected ? true : emp.selected || false,
      }));

      this.setState({
        trainingEmployees: trainingEmployeesWithSelected,
        isLoading: false,
      });

      return trainingEmployeesWithSelected;
    } catch (error) {
      if (!Axios.isCancel(error)) {
        console.error('Error loading employees or training data:', error);
        this.setState({ isLoading: false });
      }
    }
  };

  filtersChanged = filters => {
    this.setState(state => ({
      initialSearchParams: {
        ...state.initialSearchParams,
        page: 0,
        trainingEmployeeModalFilters: filters,
      },
    }));
  };

  handleSelect = employeeId => {
    this.setState(({ trainingEmployees }) => ({
      trainingEmployees: trainingEmployees.map(emp =>
        emp.employeeId === employeeId ? { ...emp, selected: !emp.selected } : emp,
      ),
    }));
  };

  handleSelectAll = () => {
    this.setState(({ trainingEmployees, allSelected }) => ({
      trainingEmployees: trainingEmployees.map(x => ({ ...x, selected: !allSelected })),
      allSelected: !allSelected,
    }));
  };

  handleRegister = async employeeId => {
    if (this.state.saving) return;
    this.setState({ saving: true });

    const { training } = this.props;

    if (!training?.id) return;

    await api.base.post(`${config.appUrls.api}/api/TrainingEmployee/post`, {
      trainingId: training.id,
      employeeId,
    });
    await this.loadEmployeesWithTraining(training.id);
    this.setState({ saving: false });
  };

  changeTrainingEmployeeStatus = async (employeeId, status) => {
    if (this.state.saving) return;
    this.setState({ saving: true });

    const { training } = this.props;
    if (!training?.id) {
      this.setState({ saving: false });
      return;
    }
    await api.base.put(`${config.appUrls.api}/api/TrainingEmployee/changestatus`, {
      trainingId: training.id,
      employeeId,
      status,
    });

    this.loadEmployeesWithTraining(training.id);
    this.setState({ saving: false });
  };

  handleAddSelectedEmployeesToTraining = async () => {
    if (this.state.saving) return;
    this.setState({ saving: true });

    const { training } = this.props;
    if (!training?.id) {
      this.setState({ saving: false });
      return;
    }

    const selectedEmployees = this.state.trainingEmployees?.filter(emp => emp.selected) || [];
    const employeesToRegister = selectedEmployees.map(emp => emp.employeeId);
    if (employeesToRegister.length === 0) {
      this.setState({ saving: false });
      return;
    }

    await api.base.post(`${config.appUrls.api}/api/TrainingEmployee/registerEmployeesToTraining`, {
      trainingId: training.id,
      employeeIds: employeesToRegister,
    });

    const employees = await this.loadEmployeesWithTraining(
      training.id,
      this.state.initialSearchParams.trainingEmployeeModalFilters,
    );

    const employeesWithSelection = employees.map(emp => ({
      ...emp,
      selected: selectedEmployees.some(sel => sel.employeeId === emp.employeeId),
    }));

    this.setState({ trainingEmployees: employeesWithSelection, saving: false });
  };

  renderRow = emp => {
    const { intl } = this.props;
    return (
      <tr key={emp.employeeId}>
        <td className="checkbox-column">
          <Checkbox value={emp.selected} onClick={() => this.handleSelect(emp.employeeId)} />
        </td>
        <td>
          {emp.employeeFirstName} {emp.employeeLastName}
        </td>

        <td>
          {emp.status != null && <FormattedListValue list="trainingStatus" id={emp.status} />}
        </td>

        <td>
          {emp.trainingEmployeeDurationMinutes
            ? emp.trainingEmployeeDurationMinutes.minuteToTime()
            : ''}
        </td>
        <td>{emp.completedDate ? intl.formatDate(emp.completedDate) : ''}</td>

        <td className="text-right">
          {(emp.status == null || emp.status === 3) && (
            <a
              className="btn btn-xs btn-icon"
              title={intl.formatMessage(messages.RegisterEmployee)}
              disabled={this.state.saving}
              onClick={() => this.handleRegister(emp.employeeId)}
            >
              <UserAddIcon className="file-icon" />
            </a>
          )}

          {emp.status === 2 && (
            <a
              className="btn btn-xs btn-icon"
              title={intl.formatMessage(messages.ReopenTrainingEmployee)}
              disabled={this.state.saving}
              onClick={() => this.changeTrainingEmployeeStatus(emp.employeeId, 1)}
            >
              <UndoIcon className="file-icon" />
            </a>
          )}

          {emp.status != null && emp.status !== 2 && emp.status !== 3 && (
            <a
              className="btn btn-xs btn-icon"
              title={intl.formatMessage(messages.MarkAsComplete)}
              disabled={this.state.saving}
              onClick={() => this.changeTrainingEmployeeStatus(emp.employeeId, 2)}
            >
              <CircleCheckIcon className="file-icon" />
            </a>
          )}
          {emp.status != null && emp.status !== 2 && emp.status !== 3 && (
            <a
              className="btn btn-xs btn-icon"
              title={intl.formatMessage(messages.CancelEmployee)}
              disabled={this.state.saving}
              onClick={() => this.changeTrainingEmployeeStatus(emp.employeeId, 3)}
            >
              <CancelIcon className="file-icon" />
            </a>
          )}
        </td>
      </tr>
    );
  };

  render() {
    const { show, onClose, training, intl } = this.props;
    const { trainingEmployees, allSelected } = this.state;

    return (
      <Modal show={show} onHide={onClose} dialogClassName="training-employee-modal">
        <Modal.Header closeButton>
          <Modal.Title as="div">
            <div>{`Training: ${training.name}`}</div>
            <div className="training-subtitle">
              {`${intl.formatDate(training.startDate)} – ${intl.formatDate(training.endDate)} • ${
                training.durationHours
              } ${intl.formatMessage(messages.Hours)}`}
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/*  */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem', // Adds some space below the filter bar
            }}
          >
            <TrainingEmployeeModalFilters
              filters={this.state.initialSearchParams.trainingEmployeeModalFilters}
              onFilterChange={this.filtersChanged}
              intl={intl}
              addSelectedEmployeesButton={{
                onClick: this.handleAddSelectedEmployeesToTraining,
                disabled: this.state.saving,
                label: intl.formatMessage(messages.PlanTrainingForSelectedEmployees),
                icon: <AddIcon />,
              }}
            />
          </div>

          {/*  */}
          <table className="table table-grid table-hover registration-table">
            <thead>
              <tr>
                <th className="checkbox-column">
                  <Checkbox
                    extraClass="margin5R"
                    onClick={this.handleSelectAll}
                    value={allSelected}
                  />
                </th>
                <th>{intl.formatMessage(messages.Name)}</th>
                <th>{intl.formatMessage(messages.Status)}</th>
                <th>{intl.formatMessage(messages.RegisteredTrainingHours)}</th>
                <th>{intl.formatMessage(messages.CompletedDate)}</th>
                <th className="text-right" />
              </tr>
            </thead>

            <tbody>{trainingEmployees.map(this.renderRow)}</tbody>
          </table>
        </Modal.Body>
      </Modal>
    );
  }
}

export default injectIntl(TrainingEmployeeModal);
